import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './styles.module.scss';
import { faCheck, faChevronLeft, faFlag, faGear, faPaperPlane, faTrash, faUserFriends, faUserSlash, faWalking } from '@fortawesome/free-solid-svg-icons';
import Image from 'next/image';
import { use, useEffect, useRef, useState } from 'react';

import { BarLoader, PuffLoader, PulseLoader } from 'react-spinners'
import axios from 'axios';
import errorMsg from '../../Functions/errorMsg';

import { useSession } from 'next-auth/react'
import { useSocket } from '../../Context/socket';

import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import successMsg from '../../Functions/successMsg';

export default function MessangerComponent({ convoID, blockedUsers, requestingUserID, closeMessanger, handleConvoViewed, handleNewMessage, handleConvoDeleted, handleUserBlockingMethod }) {

    const router = useRouter();

    const { data: session, status } = useSession();

    const { socket, socketConnected, onlineUsers } = useSocket();

    const [otherUserPfpLoaded, setOtherUserPfpLoaded] = useState(false);

    const [loading, setLoading] = useState(false);
    const [convoData, setConvoData] = useState(null);

    const messagesContainerRef = useRef(null);

    useEffect(() => {
        if(!messagesContainerRef) return;
        if(!convoData) return;
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    }, [messagesContainerRef, convoData]);

    useEffect(() => {
        if(!convoID) return;
        if(!requestingUserID) return;
        if(loading === true) return;
        if(convoData !== null) return;
        if(!session) return;
        if(status === 'loading') return;
        if(status === 'unauthenticated') return router.push('/account/login');

        async function getConvoData() {
            setLoading(true);
            const result = await axios.post(`${window.location.origin}/api/messaging/getConversation`, {
                requestingUserID: requestingUserID,
                convoID: convoID
            }).catch((error) => {
                errorMsg(error?.response?.data?.status || error.toString());
            })

            if(result && result.status === 200) {
                const data = result.data;
                setConvoData(data);
                handleConvoViewed(data._id, session.user.token.username);
            }

            setLoading(false);
        }

        getConvoData()

    }, [requestingUserID, convoID])

    const [otherUserTyping, setOtherUserTyping] = useState(false);

    let typingTimer;

    const handleTyping = (e) => {
        const getReceivingUser = convoData.sender === session.user.token.username ? convoData.receiver : convoData.sender;
        const getRequestingUser = session.user.token.username;
        socket.emit('send-user-typing', { isTyping: true, sender: getRequestingUser, receiver: getReceivingUser });

        clearTimeout(typingTimer);
        typingTimer = setTimeout(() => {
            socket.emit('send-user-typing', { isTyping: false, sender: getRequestingUser, receiver: getReceivingUser });
        }, 3000);
    };

    useEffect(() => {
        if(!socket || !socketConnected || !convoData) return;

        socket.on('user-typing', (data) => {
            setOtherUserTyping(data.isTyping);
        })

        return () => {
            socket.off('user-typing');
        };
    }, [socket, socketConnected, convoData])

    useEffect(() => {
        if(!socket || !socketConnected || !convoData || !requestingUserID || !convoID || !messagesContainerRef) return;

        const getReceivingUser = convoData.sender === session.user.token.username ? convoData.receiver : convoData.sender;
        const getRequestingUser = session.user.token.username;

        if(!getReceivingUser || !getRequestingUser) return;

        socket.emit('join-chat', { roomID: convoID, username: session.user.token.username, receivingUser: getReceivingUser });

        socket.on('user-typing', (data) => {
            setOtherUserTyping(data);
            if (messagesContainerRef.current) {
                messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            }
        })

        return () => {
            socket.emit('leave-chat', { roomID: convoID });
            socket.off('user-typing');
        };
    }, [socket, socketConnected, convoData, requestingUserID, convoID. messagesContainerRef])

    useEffect(() => {
        if(!socket || !socketConnected || !convoData) return;
        socket.on('getMsgMessanger', ({ convoID, sender, receiver, message, timestamp, viewed }) => {

            const updatedConvoData = { ...convoData };
            if(convoData._id === convoID) {
                updatedConvoData.messages.push({
                    sender: sender,
                    message: message,
                    timestamp: timestamp,
                    viewed: viewed
                });
    
                setConvoData(updatedConvoData);
            }

        });

        return () => {
            socket.off('getMsgMessanger');
        };
    }, [socket, socketConnected, convoData, setConvoData])

    function loadingState() {
        if(loading) return true;
        if(!convoData) return true;
        if(!session) return true;
        if(status === 'loading') return true;
        if(status === 'unauthenticated') return closeMessanger();
        return false;
    }

    function returnOtherUserData() {
        const getOtherUser = convoData.receiver === session.user.token.username ? convoData.sender : convoData.receiver;
        const getOtherUserImage = convoData.otherUserPfp;

        return { username: getOtherUser, pfp: getOtherUserImage }
    }

    const messageRef = useRef(null);

    useEffect(() => {
        if(!messageRef || !messageRef.current || !convoData) return;
        messageRef.current.focus();
    }, [messageRef, convoData])

    const [pendingMessage, setPendingMessage] = useState(false);

    async function handleSendMessage() {
        if(pendingMessage === true) return;
        if(!messageRef) return;
        const msg = messageRef.current.value;
        if(msg.length === 0) return errorMsg("Please enter a message before sending.");
        if(msg.length > 500) return errorMsg("Please use 500 characters or less");

        if(!session) return;
        if(status === 'loading') return;
        if(status === 'unauthenticated') return;

        setPendingMessage(true);

        const result = await axios.post(`${window.location.origin}/api/messaging/send`, {
            userRequestingID: session.user.token.id,
            convoID: convoID,
            message: msg
        }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString());
        })

        if(result && result.status === 200) {
            messageRef.current.value = '';
            messageRef.current.focus();
            
            const messageData = result.data;

            const singularMessageData = {
                sender: messageData.sender,
                message: messageData.message,
                timestamp: messageData.timestamp,
                viewed: messageData.viewed
            }

            const emitMessageData = {
                convoID: messageData.convoID,
                sender: messageData.sender,
                receiver: messageData.receiver,
                message: messageData.message,
                timestamp: messageData.timestamp,
                viewed: messageData.viewed
            }

            if(convoID === messageData.convoID) {
                if(socket && socketConnected) {
                    socket.emit('sendMessage', emitMessageData)
                    clearTimeout(typingTimer);
                    socket.emit('send-user-typing', { isTyping: false, sender: messageData.sender, receiver: messageData.receiver });
                }
    
                const updatedConvoData = {...convoData, messages: [...convoData.messages, singularMessageData]};
                setConvoData(updatedConvoData);
                handleNewMessage(messageData.convoID, singularMessageData);
            }


        }

        setPendingMessage(false);

    }

    function handleConvoReport(convoID, reportData) {
        const updatedConvoData = [ ...convoData ];

        const conversation = updatedConvoData.find(convo => convo._id === convoID);

        if (conversation) {
            conversation.reports.push(reportData);
            setConvoData(updatedConvoData);
            successMsg("Your report has been successfully submitted.");
        }
    }
    const [otherUserOnline, setOtherUserOnline] = useState(null);

    useEffect(() => {
        if(loadingState() === true) return;
        if(onlineUsers.includes(returnOtherUserData().username)) {
            setOtherUserOnline(true);
        } else {
            setOtherUserOnline(false);
        }
    }, [loadingState(), onlineUsers])

    const [openSettings, setOpenSettings] = useState(false);

  return (
    <div id={styles.messangerComponent}>
        { openSettings && <ChatSettings convoID={convoID} handleUserBlockingMethod={handleUserBlockingMethod} blockedUsers={blockedUsers} getIsConvoReported={convoData.reports.some((report) => report.reportingUser === session.user.token.username)} otherUserUsername={returnOtherUserData().username} handleConvoReport={handleConvoReport} closeSettings={() => setOpenSettings(false)} handleConvoDeleted={handleConvoDeleted} /> }
        { loadingState() && <FontAwesomeIcon onClick={closeMessanger} icon={faChevronLeft} id={styles.back} /> }
        { loadingState() && <section id={styles.loading}> <BarLoader color='#4169E1' width={200} height={6} /> </section> }
        { !loadingState() && <section id={styles.contentWrapper}>
            <section id={styles.header}>
                <FontAwesomeIcon onClick={closeMessanger} icon={faChevronLeft} id={styles.back} />
                <div>
                    <span style={{display: otherUserPfpLoaded === true ? 'none' : 'flex'}}> <PuffLoader size={20} color='#4169E1' /> </span>
                    <Image sizes='auto auto' fill objectFit='cover' src={returnOtherUserData().pfp} alt="Profile Picture Waco Exchange" onLoadingComplete={() => setOtherUserPfpLoaded(true)} />
                </div>
                <h1>{returnOtherUserData().username} <b id={styles.status} style={{color: otherUserOnline === true ? '#0da00d' : 'white'}} >{'\u00B7'}</b></h1>
                <div onClick={() => setOpenSettings(true)}>
                    <motion.span animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 2, ease: 'linear' }}><FontAwesomeIcon icon={faGear} color='#424242' size='lg' /></motion.span>
                </div>
            </section>
            <section ref={messagesContainerRef} id={styles.messages}>
                <div id={styles.msgContainer}><textarea ref={messageRef} onKeyDown={handleTyping} disabled={pendingMessage} placeholder="Your message" /><button onClick={handleSendMessage}>{ pendingMessage === true ? <PuffLoader color='white' size={30} /> : <FontAwesomeIcon icon={faPaperPlane} /> }</button></div>
                { convoData.messages.map((msg, index) => {
                    return msg.sender === session.user.token.username ? <MyMessageCard key={index} data={msg} /> : <OtherUserMessageCard key={index} data={msg} pfp={returnOtherUserData().pfp} />
                }) }
                { otherUserTyping && <OtherUserTyping pfp={returnOtherUserData().pfp} /> }
            </section>
        </section> }

    </div>
  )
}

const ChatSettings = ({ convoID, otherUserUsername, closeSettings, handleConvoDeleted, handleConvoReport, handleUserBlockingMethod, getIsConvoReported, getIsUserBlocked }) => {

    const { data: session, status } = useSession();

    const [isConvoReported, setIsConvoReported] = useState(getIsConvoReported);
    const [isUserBlocked, setIsUserBlocked] = useState(getIsUserBlocked);

    const [openReportBox, setOpenReportBox] = useState(false);


    const [pendingDelete, setPendingDelete] = useState(false);
    const [pendingBlock, setPendingBlock] = useState(false);

    async function handleBlockUser() {
        if(pendingBlock) return;
        setPendingBlock(true);
        const result = await axios.post(`${window.location.origin}/api/account/blockHelpers/blockHandler`, {
            requestingUserID: session.user.token.id,
            toBlockUser: otherUserUsername,
            action: isUserBlocked ? 'unblock' : 'block'
        }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString());
        })

        if(result && result.status === 200) {
            setIsUserBlocked(!isUserBlocked);
            handleUserBlockingMethod(otherUserUsername);
        }
        
        setPendingBlock(false);
    }

    async function handleDeleteChat() {
        if(pendingDelete) return;
        setPendingDelete(true);

        /*
            const requestingUserID = req.body.requestingUserID;
            const conversationID = req.body.conversationID;
        */

        const result = await axios.post(`${window.location.origin}/api/messaging/helpers/delete`, {
            requestingUserID: session.user.token.id,
            conversationID: convoID
        }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString());
        })

        if(result && result.status === 200) {
            handleConvoDeleted(convoID);
        }

        setPendingDelete(false);
    }

    const [pendingReport, setPendingReport] = useState(false);

    async function handleReportConversation(reportReason, reportText) {
        if(pendingReport) return;
        setPendingReport(true);

        const creationDate = new Date().toISOString();

        const result = await axios.post(`${window.location.origin}/api/TFSSystem/createReport`, {
            conversationID: convoID,
            requestingUserID: session.user.token.id,
            origin: 'Messanger',
            reportedUser: otherUserUsername,
            reasonType: reportReason,
            message: reportText,
            creationDate: creationDate
        }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString());
        })

        if(result && result.status === 200) {
            setIsConvoReported(true);

            var createReportData = {
                reportID: result.data,
                reportOrigin: 'Messanger',
                reportingUser: otherUserUsername,
                reportedUser: session.user.token.username,
                reportReason: reportReason,
                reportMessage: reportText,
                verdict: null,
                creationDate: creationDate
            };

            handleConvoReport(convoID, createReportData);
        }

        setPendingReport(false);

    }

    return (
        <section id={styles.chatSettings}>
            { openReportBox && <ReportConversation pendingReport={pendingReport} handleReportConversation={handleReportConversation} closeReport={() => setOpenReportBox(false)} /> }
            <div>
                <div>
                    <FontAwesomeIcon onClick={closeSettings} icon={faChevronLeft} id={styles.back} />
                    <h1>Chat Settings</h1>
                </div>
                <div>
                    <button disabled={isConvoReported} onClick={() => setOpenReportBox(true)}>{ isConvoReported ? <>Conversation Reported <FontAwesomeIcon style={{marginLeft: '7px'}} icon={faCheck} color='green' /></> : <><FontAwesomeIcon icon={faFlag} id={styles.icon} />{pendingReport ? <PulseLoader color='black' /> : 'Report Conversation' }</> }</button>
                    <button onClick={handleBlockUser}>{ pendingBlock ? <PulseLoader color='black' /> : <> <FontAwesomeIcon icon={isUserBlocked ? faUserFriends : faUserSlash} id={styles.icon} /> { isUserBlocked ? 'Unblock User' : 'Block User' } </> }</button>
                    <button onClick={handleDeleteChat}><FontAwesomeIcon icon={faWalking} id={styles.icon} />{pendingDelete ? <PulseLoader color='black' /> : 'Leave Chat'}</button>
                </div>
            </div>
        </section>
    )
}

const ReportConversation = ({ closeReport, pendingReport, handleReportConversation }) => {

    const reportTextRef = useRef(null);
    const [reportReason, setReportReason] = useState(null);

    function preHandleSubmit() {
        const reportText = reportTextRef.current.value;
        if(!reportText) return errorMsg("Could not submit your report, please submit your report to us contact@wacoexchange.com");
        if(!reportReason) return errorMsg("Please select a reason before submitting");

        closeReport();
        handleReportConversation(reportReason, reportText);
    }

    return (
        <section id={styles.reportBox}>
            <div>
                <div>
                    <FontAwesomeIcon onClick={closeReport} icon={faChevronLeft} id={styles.back} />
                    <h1>Report Conversation</h1>
                </div>
                <div>
                    <div id={styles.types}>
                        <button onClick={() => setReportReason("Inappropriate Content")} disabled={pendingReport} style={reportReason === "Inappropriate Content" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Inappropriate Content</button>
                        <button onClick={() => setReportReason("Spam or Scams")} disabled={pendingReport} style={reportReason === "Spam or Scams" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Spam or Scams</button>
                        <button onClick={() => setReportReason("Safety Concerns")} disabled={pendingReport} style={reportReason === "Safety Concerns" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Safety Concerns</button>
                        <button onClick={() => setReportReason("Illegal Activity")} disabled={pendingReport} style={reportReason === "Illegal Activity" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Illegal Activity</button>
                        <button onClick={() => setReportReason("Privacy Violation")} disabled={pendingReport} style={reportReason === "Privacy Violation" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Privacy Violation</button>
                        <button onClick={() => setReportReason("Offensive Behavior")} disabled={pendingReport} style={reportReason === "Offensive Behavior" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Offensive Behavior</button>
                    </div>

                    <textarea maxLength={500} disabled={pendingReport} ref={reportTextRef} style={{display: reportReason !== null ? 'flex' : 'none'}} placeholder='Please give us some detail...' />
                    <button disabled={pendingReport} onClick={preHandleSubmit} style={{display: reportReason !== null ? 'flex' : 'none'}}>Submit Report</button>
                </div>
            </div>
        </section>
    )
}

const OtherUserTyping = ({ pfp }) => {

    return (
        <div id={styles.otherUserTyping}>
            <Image width={25} height={25} objectFit='cover' src={pfp} alt={pfp} />
            <section>
                <PulseLoader speedMultiplier={1.1} color='#0F52BA' />
            </section>
        </div>
    )
}

const MyMessageCard = ({ data }) => {

    return (
        <div className={styles.myUser}>
            <section>
                <p>{data.message}</p>
                <h6>{formatTimestamp(data.timestamp)}</h6>
            </section>
        </div>
    )
}

const OtherUserMessageCard = ({ data, pfp }) => {

    return (
        <div className={styles.otherUser}>
            <Image width={25} height={25} objectFit='cover' src={pfp} alt={pfp} />
            <section>
                <p>{data.message}</p>
                <h6>{formatTimestamp(data.timestamp)}</h6>
            </section>
        </div>
    )
}

function formatTimestamp(timestamp) {
    const date = new Date(timestamp);
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 12-hour clock, 0 becomes 12
    const formattedTime = `${hours}:${minutes < 10 ? '0' + minutes : minutes} ${ampm}`;
    return formattedTime;
}